/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import React from 'react';
import _ from 'lodash';
import { decorate, computed, action, runInAction, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Container, Icon, Label, Popup } from 'semantic-ui-react';

import { gotoFn } from '@amzn/base-ui/dist/helpers/routing';
import { swallowError } from '@amzn/base-ui/dist/helpers/utils';
import { isStoreLoading, isStoreEmpty, isStoreNotEmpty, isStoreError } from '@amzn/base-ui/dist/models/BaseStore';
import { displayError, displaySuccess } from '@amzn/base-ui/dist/helpers/notification';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

class AnnouncementsList extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.deletingId = null;
    });
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    const store = this.announcementsStore;
    swallowError(store.load());
  }

  get announcementsStore() {
    return this.props.announcementsStore;
  }

  handleCreate = event => {
    event.preventDefault();
    event.stopPropagation();
    const goto = gotoFn(this);
    goto('/announcements/create');
  };

  handleEdit = (event, id) => {
    event.preventDefault();
    event.stopPropagation();
    const goto = gotoFn(this);
    goto(`/announcements/edit/${id}`);
  };

  handleDelete = async (event, announcement) => {
    event.preventDefault();
    event.stopPropagation();
    this.deletingId = announcement.id;
    try {
      await this.announcementsStore.deleteAnnouncement(announcement.id, announcement.rev);
      displaySuccess('Announcement deleted successfully');
    } catch (error) {
      displayError(error);
    } finally {
      runInAction(() => {
        this.deletingId = null;
      });
    }
  };

  render() {
    const store = this.announcementsStore;
    let content = null;

    if (isStoreError(store)) {
      content = <ErrorBox error={store.error} className="p0" />;
    } else if (isStoreLoading(store)) {
      content = <ProgressPlaceHolder segmentCount={1} />;
    } else if (isStoreEmpty(store)) {
      content = this.renderEmpty();
    } else if (isStoreNotEmpty(store)) {
      content = this.renderMain();
    }

    return (
      <Container className="mt3 animated fadeIn">
        {this.renderTitle()}
        {content}
      </Container>
    );
  }

  renderTitle() {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
        <button className="workspace-action-btn workspace-action-btn-primary" onClick={this.handleCreate}>
          <Icon name="plus" style={iconStyle} /> Create Announcement
        </button>
      </div>
    );
  }

  renderEmpty() {
    return (
      <div className="workspace-modern-table-empty">
        <Icon name="newspaper" style={{ fontSize: '2.5em', color: '#d1d5db', marginBottom: '12px' }} />
        <div className="workspace-modern-table-empty-title">No Announcements</div>
        <div className="workspace-modern-table-empty-text">To create an announcement, click Create Announcement.</div>
      </div>
    );
  }

  renderMain() {
    const list = this.announcementsStore.list;
    const active = _.filter(list, item => item.status === 'active');
    const hidden = _.filter(list, item => item.status !== 'active');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {_.map(active, item => this.renderAnnouncementCard(item))}
        {hidden.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
              <Icon name="eye slash" style={{ color: '#9ca3af', fontSize: '1.1em', margin: 0 }} />
              <span style={{ fontWeight: 600, fontSize: '1.05em', color: '#6b7280' }}>Hidden</span>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
            </div>
            {_.map(hidden, item => this.renderAnnouncementCard(item))}
          </>
        )}
      </div>
    );
  }

  renderAnnouncementCard(announcement) {
    const statusColor = announcement.status === 'active' ? '#16a34a' : '#6b7280';
    const statusBg = announcement.status === 'active' ? '#dcfce7' : '#f3f4f6';
    const isDeleting = this.deletingId === announcement.id;

    return (
      <div className="workspace-card" key={announcement.id}>
        <div className="workspace-card-accent" style={{ background: statusColor }} />
        <div className="workspace-card-content">
          <div className="workspace-card-header">
            <div style={{ flex: 1 }}>
              <h3 className="workspace-card-title">
                <Label color={announcement.color} size="tiny" style={{ marginRight: '8px' }}>
                  {announcement.type}
                </Label>
                {announcement.content.length > 100
                  ? `${announcement.content.substring(0, 100)}...`
                  : announcement.content}
              </h3>
            </div>
            <Popup
              trigger={
                <span className="workspace-status-badge" style={{ background: statusBg, color: statusColor }}>
                  <Icon name="circle" style={iconStyle} />
                  {announcement.statusDisplay}
                </span>
              }
              content={announcement.status === 'active' ? 'Visible on dashboard' : 'Hidden from dashboard'}
              size="small"
              inverted
            />
          </div>

          <div className="workspace-meta-row">
            <span className="workspace-meta-item">
              <Icon name="sort numeric ascending" style={iconStyle} />
              Priority: {announcement.priority}
            </span>
            <span className="workspace-meta-item">
              <Icon name="paint brush" style={iconStyle} />
              Colour: {announcement.color}
            </span>
            <span className="workspace-meta-item">
              <Icon name="hashtag" style={iconStyle} />
              {announcement.id}
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
            <button
              className="workspace-action-btn workspace-action-btn-secondary"
              onClick={e => this.handleEdit(e, announcement.id)}
            >
              <Icon name="edit" style={iconStyle} /> Edit
            </button>
            <button
              className="workspace-action-btn workspace-action-btn-secondary"
              disabled={isDeleting}
              onClick={e => this.handleDelete(e, announcement)}
              style={{ color: '#dc2626' }}
            >
              <Icon name="trash" style={iconStyle} /> {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

decorate(AnnouncementsList, {
  announcementsStore: computed,
  deletingId: observable,
  handleCreate: action,
  handleEdit: action,
  handleDelete: action,
});

export default inject('announcementsStore')(withRouter(observer(AnnouncementsList)));
